import { useEffect, useState } from "react";
import { Layout, Card, Space, message, DatePicker, Form, Alert, Typography, Table, Col, Row } from "antd";
import FormItem from "antd/lib/form/FormItem";
import { apiCall } from "../utils/Api";
import LayoutHeader from "../layouts/partials/LayoutHeader";
import moment from "moment";
import { Bar, Line } from '@ant-design/charts';

export default function TwilioUsagePage(props) {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("")
    const [remainingBalance, setRemainingBalance] = useState(0)
    const [sent, setSent] = useState(0)
    const [received, setReceived] = useState(0)
    const [chartData, setChartData] = useState([])
    const [topUpData, setTopUpData] = useState([])
    const [monthlyBillingData, setMonthlyBillingData] = useState([])
    const locale = window.sitesettings.is_canadian ? "en-CA" : "en-US"
    const currency = window.sitesettings.is_canadian ? "CAD" : "USD"

    const initFilters = {
        start_date: moment().add(-1, "day").startOf("month").startOf("day").unix(),
        end_date: moment().add(-1, "day").endOf("day").unix(),
    }

    const [filters, setFilters] = useState({ ...initFilters });

    // Add an extra day to the count (startOf, endOf doesn't inc the day count)
    const range = moment(filters.end_date * 1000).endOf("day").diff(moment(filters.start_date * 1000).add(-1, "day").startOf("day"), "days");

    // Show yesterday, today in leau of date
    const customFormat = (value) => {
        let _diff = moment().startOf("day").diff(value.startOf("day"), "days");
        if (_diff === 1) { return "Yesterday"; }
        if (_diff === 1) { return "Today"; }
        return value.format("ddd MMMD-YY");
    }

    const disabledEnding = (current) => {
        return current && current < moment(filters.start_date * 1000).add(-1, "day").endOf('day');
    };

    const disabledBeginning = (current) => {
        return current && current > moment(filters.end_date * 1000).endOf('day');
    };

    const getStatus = () => {
        apiCall("twilio/status/get", {}, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error);
                } else if (_result.status !== "closed" && _result.status !== undefined) {
                    getBilling();
                    getStoreUsage();
                    getDailyUsage();
                    getTopUps();
                    getMonthlyBilling();
                }
                setStatus(_result.status);
            } else {
                message.error(_result.error);
            }
        });
    }

    const getBilling = () => {
        apiCall("twilio/balance/get", {}, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error);
                } else {
                    setRemainingBalance(_result.balance);
                }
            } else {
                message.error(_result.error);
            }
        });
    }

    const getMonthlyBilling = () => {
        apiCall("twilio/billing/monthly/get", {}, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error);
                } else {
                    setMonthlyBillingData(_result.rows);
                }
            } else {
                message.error(_result.error);
            }
        });
    }

    const getTopUps = () => {
        apiCall("twilio/topups/get", {}, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error)
                } else {
                    setTopUpData(_result.rows)
                }
            } else {
                message.error(_result.error);
            }
        });
    }

    const getStoreUsage = () => {
        apiCall("twilio/usage/get", { start_date: filters.start_date, end_date: filters.end_date }, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error);
                } else {
                    setSent(_result.sent);
                    setReceived(_result.received);
                }
            } else {
                message.error(_result.error);
            }
        });
    }

    const getDailyUsage = () => {
        apiCall("twilio/usage/daily/get", { start_date: filters.start_date, end_date: filters.end_date }, (_status, _result) => {
            if (_status) {
                if (_result.error) {
                    message.error(_result.error);
                } else {
                    setChartData(_result.rows);
                    setLoading(false);
                }
            } else {
                message.error(_result.error);
            }
        });
    }

    useEffect(() => {
        getStatus();
    }, []);

    useEffect(() => {
        getStoreUsage()
        getDailyUsage()
    }, [filters])

    return (
        <>
            {remainingBalance < 0 ? <Alert style={{ "marginBottom": "20px" }} type="warning" description="You have used all of your available credit, please email us at mail@bookmanager.com to top up." /> : <></>}
            <LayoutHeader
                title={"Texting Usage"}
                description={<div className="float-flex">
                    <div>Use this page to review your remaining balance for texting, and its latest usage.</div>
                    <div><a href="https://support.bookmanager.com/tutorials/point-of-sale/sending-notifications/texting-pricing-faq" target="_blank">View <em>"Texting Pricing & FAQ"</em> on Help Centre</a></div>
                </div>}
            />
            <Layout className="layout" >
                <Layout.Content style={{ "padding": "0px" }} >
                    <div style={{ "padding": "20px" }}>
                        {status !== "closed" ?
                            <>
                                <Card>
                                    <Row>
                                        <Col>
                                            <Typography.Title style={{ "lineHeight": "17px" }} level={5}>
                                                <div>
                                                    Summary
                                                </div>
                                            </Typography.Title>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col span={6}>
                                            <Space direction="vertical">
                                                <small>
                                                    Remaining Balance
                                                </small>
                                                <p style={{ color: (remainingBalance < 0) ? '#e33535' : '#3f8600' }}>
                                                    {Intl.NumberFormat(locale, {
                                                        style: 'currency',
                                                        currency: currency,
                                                        currencyDisplay: 'narrowSymbol',
                                                    }).format(remainingBalance)}
                                                </p>
                                            </Space>
                                        </Col>
                                        <Col span={12} offset={6}>
                                            <Space size={16}>
                                                <Form layout="vertical" size="small">
                                                    <Space size={8}>
                                                        <FormItem value={moment(filters.start_date * 1000)} label={<small>Beginning</small>}>
                                                            <DatePicker disabledDate={disabledBeginning} style={{ "fontSize": "10px" }} format={customFormat} onChange={(mom) => { setLoading(true); setFilters({ ...filters, "start_date": mom.startOf("day").unix() }) }} value={moment(filters.start_date * 1000)} />
                                                        </FormItem>
                                                        <FormItem label={<small>Ending <span>({range.toString()} days)</span></small>}>
                                                            <DatePicker format={customFormat} onChange={(mom) => {
                                                                // If ending is less that starting, shift the start-date by the range
                                                                if (disabledEnding(mom)) {
                                                                    let _days = range - 1;
                                                                    setFilters({ ...filters, "end_date": mom.endOf("day").unix(), "start_date": mom.add(-_days, "days").startOf("day").unix() })
                                                                } else {
                                                                    setFilters({ ...filters, "end_date": mom.endOf("day").unix() })
                                                                }
                                                                setLoading(true)
                                                            }} value={moment(filters.end_date * 1000)} />
                                                        </FormItem>
                                                    </Space>
                                                </Form>
                                                <Space direction="vertical">
                                                    <small>
                                                        Messages sent
                                                    </small>
                                                    <p>
                                                        {sent}
                                                    </p>
                                                </Space>
                                                <Space direction="vertical">
                                                    <small>
                                                        Messages received
                                                    </small>
                                                    <p>
                                                        {received}
                                                    </p>
                                                </Space>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>
                                <Row>
                                    <Col span={6}>
                                        <Card style={{ height: '100%', width: '100%' }}>
                                            <Row>
                                                <Col span={24}>
                                                    <Typography.Title style={{ "lineHeight": "17px" }} level={5}>
                                                        <div>
                                                            Top up history
                                                        </div>
                                                    </Typography.Title>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col span={24}>
                                                    <Table
                                                        size="small"
                                                        style={{ height: '100%' }}
                                                        columns={[
                                                            {
                                                                title: "Charge",
                                                                dataIndex: "tb_charge",
                                                                key: "tb_charge",
                                                                render: (charge) => Intl.NumberFormat(locale, {
                                                                    style: 'currency',
                                                                    currency: currency,
                                                                    currencyDisplay: 'narrowSymbol',
                                                                }).format(charge)
                                                            },
                                                            {
                                                                title: "Date",
                                                                dataIndex: "datecreated",
                                                                key: "datecreated"
                                                            },
                                                        ]}
                                                        dataSource={topUpData}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                    <Col span={6}>
                                        <Card style={{ height: '100%', width: '100%' }}>
                                            <Row>
                                                <Col span={24}>
                                                    <Typography.Title style={{ "lineHeight": "17px" }} level={5}>
                                                        <div>
                                                            Charges by Month
                                                        </div>
                                                    </Typography.Title>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col span={24}>
                                                    <Bar
                                                        autoFit={true}
                                                        data={monthlyBillingData}
                                                        xField={"charge"}
                                                        yField={"month"}
                                                        label={{
                                                            formatter: (data) => {
                                                                return Intl.NumberFormat(locale, {
                                                                    style: 'currency',
                                                                    currency: currency,
                                                                    currencyDisplay: 'narrowSymbol',
                                                                }).format(data.charge)
                                                            }
                                                        }}
                                                        tooltip={{
                                                            formatter: (data) => {
                                                                return {
                                                                    name: 'charge',
                                                                    value: Intl.NumberFormat(locale, {
                                                                        style: 'currency',
                                                                        currency: currency,
                                                                        currencyDisplay: 'narrowSymbol',
                                                                    }).format(data.charge)
                                                                };
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card style={{ height: '100%', width: '100%' }}>
                                            <Typography.Title style={{ "lineHeight": "17px" }} level={5}>
                                                <div>
                                                    Messages Sent and Recieved by Day
                                                </div>
                                            </Typography.Title>
                                            <Line
                                                width={'100%'}
                                                loading={loading}
                                                data={chartData}
                                                xField="date"
                                                yField="count"
                                                seriesField="type"
                                                xAxis={{
                                                    type: 'time'
                                                }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                            :
                            <Card>
                                <p>
                                    You do not currently have active Twilio integration. Contact customer support to get started.
                                </p>
                            </Card>
                        }
                    </div>
                </Layout.Content>
            </Layout>
        </>
    )
}